import { useFormikContext } from 'formik';
import { ReactNode, useCallback } from 'react';
import { TooltipProps } from 'ud-ui/components/tippy';
import UDFormFieldWrapper from 'ud-form/wrapper';
import HighlightWrapper from '../highlight-wrapper';
import { UDFormDateInputProps } from '.';
import { UDDateRangeInput, UDDateRangePart, UDDateRangeValue } from './component';

type Props = {
  startName: string;
  endName: string;
  startLabel?: string;
  endLabel?: string;
  startTooltip?: TooltipProps;
  endTooltip?: TooltipProps;
  highlightStart?: boolean;
  highlightEnd?: boolean;
} & Omit<UDFormDateInputProps, 'name' | 'label' | 'highlight'>;

const toIso = (date: Date | null) => (date ? date.toISOString() : null);

/**
 * Пара дат «начало — конец» в Formik поверх `UDDateRangeInput`: общий календарь-диапазон,
 * у каждой даты своя подпись, тултип и подсветка. В форму уходят строки ISO или `null` —
 * как у `UDFormDateInput`.
 */
const UDFormDateRange = (props: Props) => {
  const {
    startName,
    endName,
    startLabel,
    endLabel,
    startTooltip,
    endTooltip,
    labelProps,
    containerProps,
    disabled,
    minDate,
    autoComplete,
    highlightStart,
    highlightEnd,
  } = props;

  const { getFieldProps, getFieldMeta, setFieldValue } = useFormikContext();

  const startMeta = getFieldMeta<string | null>(startName);
  const endMeta = getFieldMeta<string | null>(endName);

  const handleChange = useCallback(
    ({ start, end }: UDDateRangeValue) => {
      setFieldValue(startName, toIso(start));
      setFieldValue(endName, toIso(end));
    },
    [startName, endName, setFieldValue],
  );

  const renderField = (part: UDDateRangePart, field: ReactNode) => {
    const isStart = part === 'start';
    const tooltip = isStart ? startTooltip : endTooltip;

    return (
      <UDFormFieldWrapper
        name={isStart ? startName : endName}
        label={isStart ? startLabel : endLabel}
        labelProps={labelProps}
        tooltip={tooltip?.tooltip}
        tooltipProps={tooltip?.tooltipProps}
      >
        {() => (
          <HighlightWrapper highlight={isStart ? highlightStart : highlightEnd} className="w-full">
            {field}
          </HighlightWrapper>
        )}
      </UDFormFieldWrapper>
    );
  };

  return (
    <UDDateRangeInput
      className={containerProps?.className}
      startDate={getFieldProps(startName).value}
      endDate={getFieldProps(endName).value}
      onChange={handleChange}
      minDate={minDate}
      disabled={disabled}
      hasErrorStart={!!startMeta.error}
      hasErrorEnd={!!endMeta.error}
      validStart={!startMeta.error && startMeta.touched && !!startMeta.value}
      validEnd={!endMeta.error && endMeta.touched && !!endMeta.value}
      startProps={{ name: startName, autoComplete, onBlur: getFieldProps(startName).onBlur }}
      endProps={{ name: endName, autoComplete, onBlur: getFieldProps(endName).onBlur }}
      renderField={renderField}
    />
  );
};

export default UDFormDateRange;
